import React, { useRef, useEffect, useState } from "react";

// Minimal manual logo editor: text + background color + border + optional uploaded base image
const LogoCanvas = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const [state, setState] = useState(
    value || {
      text: "RAW",
      subtext: "COMPANY",
      bg: "#050505",
      fg: "#ffffff",
      accent: "#ff3333",
      shape: "square", // square, circle, split
      fontSize: 72,
    }
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = state.bg;
    ctx.fillRect(0, 0, W, H);

    // Shape
    ctx.fillStyle = state.accent;
    if (state.shape === "circle") {
      ctx.beginPath();
      ctx.arc(W / 2, H / 2, Math.min(W, H) * 0.4, 0, Math.PI * 2);
      ctx.fill();
    } else if (state.shape === "split") {
      ctx.fillRect(0, 0, W / 2, H);
    } else {
      ctx.strokeStyle = state.accent;
      ctx.lineWidth = 8;
      ctx.strokeRect(40, 40, W - 80, H - 80);
    }

    // Main text
    ctx.fillStyle = state.fg;
    ctx.font = `900 ${state.fontSize}px 'Archivo Black', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(state.text, W / 2, H / 2 - 10);

    // Subtext
    ctx.fillStyle = state.accent;
    ctx.font = `700 ${Math.floor(state.fontSize / 3)}px 'JetBrains Mono', monospace`;
    ctx.fillText(state.subtext, W / 2, H / 2 + state.fontSize * 0.65);
  }, [state]);

  useEffect(() => {
    onChange && onChange(state);
  }, [state]);

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const exportB64 = () => {
    const data = canvasRef.current.toDataURL("image/png");
    return data;
  };

  // Expose via state.export on parent
  useEffect(() => {
    if (onChange) onChange({ ...state, _export: exportB64 });
    // eslint-disable-next-line
  }, [state]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      <div className="border border-zinc-800 bg-[#050505] relative grid-bg">
        <canvas ref={canvasRef} width={600} height={600} className="w-full h-auto block" data-testid="logo-canvas" />
      </div>
      <div className="space-y-4">
        <div>
          <label className="label-raw">Main Text</label>
          <input
            data-testid="logo-text"
            className="input-raw"
            value={state.text}
            onChange={(e) => update("text", e.target.value)}
            maxLength={20}
          />
        </div>
        <div>
          <label className="label-raw">Sub Text</label>
          <input
            data-testid="logo-subtext"
            className="input-raw"
            value={state.subtext}
            onChange={(e) => update("subtext", e.target.value)}
            maxLength={30}
          />
        </div>
        <div>
          <label className="label-raw">Shape</label>
          <div className="grid grid-cols-3 gap-1">
            {["square", "circle", "split"].map((s) => (
              <button
                key={s}
                data-testid={`logo-shape-${s}`}
                onClick={() => update("shape", s)}
                className={`py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${
                  state.shape === s
                    ? "bg-[#ff3333] border-[#ff3333] text-white"
                    : "border-zinc-800 text-zinc-500 hover:border-white hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label-raw">BG</label>
            <input data-testid="logo-bg" type="color" value={state.bg} onChange={(e) => update("bg", e.target.value)} className="w-full h-10 bg-transparent border border-zinc-800 cursor-pointer" />
          </div>
          <div>
            <label className="label-raw">FG</label>
            <input data-testid="logo-fg" type="color" value={state.fg} onChange={(e) => update("fg", e.target.value)} className="w-full h-10 bg-transparent border border-zinc-800 cursor-pointer" />
          </div>
          <div>
            <label className="label-raw">Accent</label>
            <input data-testid="logo-accent" type="color" value={state.accent} onChange={(e) => update("accent", e.target.value)} className="w-full h-10 bg-transparent border border-zinc-800 cursor-pointer" />
          </div>
        </div>
        <div>
          <label className="label-raw">Font Size: {state.fontSize}px</label>
          <input
            data-testid="logo-fontsize"
            type="range"
            min="30"
            max="140"
            value={state.fontSize}
            onChange={(e) => update("fontSize", parseInt(e.target.value))}
            className="w-full accent-[#ff3333]"
          />
        </div>
      </div>
    </div>
  );
};

export default LogoCanvas;
